import { useEffect, useState } from "react";

import { Button } from "components/Button";

import { SubmitButton } from "components/Button/SubmitButton";
import { getUser } from "components/utils/user";
import { deleteUser, useUserAuthenticated } from "components/utils";
import { refreshAccessToken } from "components/utils/http";
import { Modal } from "react-bootstrap";

const MODAL_SHOW_SECONDS_BEFORE_SESSION_EXPIRE = 90; // Show warning dialog seconds before actual session expiration
const MODAL_COUNTER_SECONDS = 60; // Count down seconds before logout

/**
 * Display session timeout dialog after being inactive for a while.
 */
const SessionTimeoutCheck: React.FC<{ isDialogOpen: boolean; setIsDialogOpen(open: boolean): void }> = ({
    isDialogOpen,
    setIsDialogOpen,
}) => {
    const isUserAuthenticated = useUserAuthenticated();
    const [isRefreshingToken, setIsRefreshingToken] = useState(false);

    const onShowWarning = () => {
        setIsDialogOpen(true);
    };

    const onLogout = () => {
        if (isRefreshingToken) {
            return;
        }

        setIsDialogOpen(false);
        deleteUser();
    };

    const onContinue = async () => {
        if (isRefreshingToken) {
            return;
        }

        setIsRefreshingToken(true);
        try {
            await refreshAccessToken();
            setIsDialogOpen(false);
            setIsRefreshingToken(false);
        } catch (error) {
            onLogout();
        } finally {
            setIsRefreshingToken(false);
        }
    };

    useSessionTimeout(!isDialogOpen && isUserAuthenticated, onShowWarning, onLogout);

    if (!isDialogOpen) {
        return null;
    }

    return <SessionTimeoutDialog isLoading={isRefreshingToken} onContinue={onContinue} onLogout={onLogout} />;
};

const SessionTimeoutDialog: React.FC<{ isLoading: boolean; onContinue(): void; onLogout(): void }> = ({
    isLoading,
    onContinue,
    onLogout,
}) => {
    const counter = useSessionCounter(onLogout);

    return (
        <Modal show={true} aria-labelledby="user-session">
            <Modal.Header closeButton={false}>
                <Modal.Title id="user-session">User Session</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>The session is about to expire in {counter} seconds. Continue current session?</p>
            </Modal.Body>
            <Modal.Footer>
                <SubmitButton onClick={onContinue} isSubmitting={isLoading} variant="primary" spinnerText="Submitting...">
                    Continue
                </SubmitButton>
                <Button onClick={onLogout} variant="secondary">
                    Logout
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

function useSessionTimeout(enabled: boolean, onShowWarning: () => void, onLogout: () => void) {
    useEffect(() => {
        let sessionTimeout: NodeJS.Timeout | null = null;
        const clearSessionTimeout = () => {
            if (sessionTimeout) {
                clearInterval(sessionTimeout);
                sessionTimeout = null;
            }
        };

        if (!enabled) {
            clearSessionTimeout();

            return;
        }

        sessionTimeout = setInterval(() => {
            const dateNow = Date.now();
            const sessionExpires = getUser()?.sessionExpires ?? 0;
            const timeToShowWarning = sessionExpires > 0 ? sessionExpires - MODAL_SHOW_SECONDS_BEFORE_SESSION_EXPIRE * 1000 : 0;

            if (timeToShowWarning <= 0) {
                clearSessionTimeout();
                return;
            }

            if (dateNow > timeToShowWarning) {
                onShowWarning();
                clearSessionTimeout();
                return;
            }
        }, 1000);

        return () => {
            clearSessionTimeout();
        };
    }, [onShowWarning, enabled, onLogout]);
}

function useSessionCounter(onExpire: () => void) {
    const sessionExpires = (getUser()?.sessionExpires ?? 0) - (MODAL_SHOW_SECONDS_BEFORE_SESSION_EXPIRE - MODAL_COUNTER_SECONDS) * 1000;
    const initialCounter = Math.max(Math.ceil((sessionExpires - Date.now()) / 1000), 0);
    const [counter, setCounter] = useState(initialCounter);

    useEffect(() => {
        let animationFrameId: number;
        let startTimestamp = Date.now();

        const endTimestamp = startTimestamp + initialCounter * 1000;

        const tick = () => {
            const now = Date.now();
            const secondsLeft = Math.max(Math.ceil((endTimestamp - now) / 1000), 0);
            setCounter(secondsLeft);

            if (secondsLeft > 0) {
                animationFrameId = requestAnimationFrame(tick);
            } else {
                onExpire();
            }
        };

        animationFrameId = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [onExpire, initialCounter]);

    return counter;
}

export default SessionTimeoutCheck;
